import React, { useEffect, useState, useRef } from 'react';
import Loader from '../../components/Loader';
import Policy_Navbar from './PolicyNavbar';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { MdOutlineEdit, MdDevices, MdUpload, MdClose } from "react-icons/md";
import { IoSaveSharp } from "react-icons/io5";

const Terms = () => {
    const [loader, setLoader] = useState(false);
    const [policy, setPolicy] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [htmlFile, setHtmlFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const fileInputRef = useRef(null);
    const iframeRef = useRef(null);

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getTerms();
            if (resp?.status) {
                setPolicy(resp?.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoader(false);
        }
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFileError('');

        if (!file) {
            setHtmlFile(null);
            return;
        }

        // Validate file type
        if (file.type !== 'text/html') {
            setFileError('Only HTML files are allowed');
            setHtmlFile(null);
            return;
        }

        // Validate file size (1MB max)
        if (file.size > 1 * 1024 * 1024) {
            setFileError('File size must be less than 1MB');
            setHtmlFile(null);
            return;
        }

        setHtmlFile(file);

        // Load preview in iframe
        const reader = new FileReader();
        reader.onload = (event) => {
            if (iframeRef.current) {
                const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
                iframeDoc.open();
                iframeDoc.write(event.target.result);
                iframeDoc.close();
            }
        };
        reader.readAsText(file);
    }

    const handleSave = async () => {
        if (!htmlFile) {
            setFileError('Please select an HTML file');
            return;
        }

        setLoader(true);
        try {
            const formData = new FormData();
            formData.append('html', htmlFile);

            const resp = await Calling.updateTerms(formData);
            if (resp?.status) {
                toast.success('Terms & Conditions updated successfully');
                fetchData(); // Refresh data after update
                setIsEditing(false);
                setHtmlFile(null);
            } else {
                toast.error(resp?.message || 'Failed to update terms');
            }
        } catch (err) {
            console.log(err);
            toast.error('Failed to update terms');
        } finally {
            setLoader(false);
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            // Update the file input value
            if (fileInputRef.current) {
                // Create a DataTransfer object to set the files
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                fileInputRef.current.files = dataTransfer.files;

                // Trigger the onChange event manually
                const event = new Event('change', { bubbles: true });
                fileInputRef.current.dispatchEvent(event);
            }
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            {loader ? <Loader loader={loader} /> : (
                <div className='w-full text-main-font mt-16 px-2 md:px-3 md:ml-[4.4rem]'>
                    <div className="w-full mt-4">
                        <h1 className="text-xl sm:text-2xl font-bold">Terms & Conditions</h1>
                    </div>

                    <div className='bg-main-white rounded-xl pt-3 mb-5 mt-3 overflow-hidden shadow-lg'>
                        <Policy_Navbar
                            selectState={'terms'}
                            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                            label={isEditing ? 'Save' : 'Edit'}
                            className={`px-3 sm:px-4 py-1 rounded-lg border text-sm sm:text-base ${isEditing ? 'bg-main-blue text-main-white' : 'bg-main-white text-main-delete border-main-delete'} transition hover:scale-105`}
                            icon={isEditing ? <IoSaveSharp size={16} className="sm:text-lg" /> : <MdOutlineEdit size={16} className="sm:text-lg" />}
                            btnSave={isEditing}
                        />
                        <hr className='mt-3 bg-main-border border-[1px]' />

                        <div className="w-full p-3 sm:p-5">
                            {isEditing ? (
                                <div className='w-full'>
                                    <div className='text-main-font my-2 font-bold text-sm sm:text-base'>Upload Terms & Conditions HTML File</div>

                                    {/* Drag & Drop Upload Area */}
                                    <div
                                        className={`border-2 border-dashed rounded-lg p-6 mb-4 flex flex-col items-center justify-center transition-all duration-300 ${fileError ? 'border-red-400 bg-red-50' : 'border-blue-300 hover:border-blue-500 bg-blue-50'
                                            }`}
                                        onDragOver={handleDragOver}
                                        onDrop={handleDrop}
                                    >
                                        <MdUpload className="text-blue-500 text-4xl mb-2" />
                                        <p className="text-center mb-2">
                                            Drag & drop your HTML file here or
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                                className="ml-1 text-blue-600 font-medium hover:underline focus:outline-none"
                                            >
                                                browse
                                            </button>
                                        </p>
                                        <p className="text-gray-500 text-xs text-center">
                                            Only HTML files allowed (max 1MB)
                                        </p>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".html"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                    </div>

                                    {fileError && (
                                        <p className="text-red-500 text-sm mb-2">{fileError}</p>
                                    )}

                                    {htmlFile && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex flex-col sm:flex-row sm:items-center justify-between">
                                            <div className="mb-2 sm:mb-0">
                                                <p className="text-green-700 font-medium">
                                                    File selected: {htmlFile.name}
                                                </p>
                                                <p className="text-green-600 text-sm">
                                                    Size: {(htmlFile.size / 1024).toFixed(2)} KB
                                                </p>
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setHtmlFile(null);
                                                        if (fileInputRef.current) {
                                                            fileInputRef.current.value = '';
                                                        }
                                                        // Clear iframe content
                                                        if (iframeRef.current) {
                                                            const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
                                                            iframeDoc.open();
                                                            iframeDoc.write('');
                                                            iframeDoc.close();
                                                        }
                                                    }}
                                                    className="px-3 py-1 bg-red-100 text-red-700 rounded-lg flex items-center hover:bg-red-200 transition"
                                                >
                                                    <MdClose className="mr-1" />
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Mobile Phone Preview - Always Shown */}
                                    <div className="mt-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="font-medium text-gray-700">
                                                {htmlFile ? 'Mobile Preview' : 'Mobile Preview Area'}
                                            </h3>
                                            <div className="flex items-center text-xs bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full font-medium">
                                                Mobile View
                                            </div>
                                        </div>

                                        <div className="bg-gray-800 p-6 rounded-lg flex justify-center items-center">
                                            <div className="relative">
                                                {/* Phone outer case */}
                                                <div className="absolute inset-0 bg-black rounded-[40px] shadow-2xl transform scale-[1.03] border border-gray-700"></div>

                                                {/* Phone inner frame */}
                                                <div className="relative bg-black rounded-[36px] overflow-hidden border-[8px] border-black" style={{ width: '375px', height: '700px' }}>
                                                    {/* Notch */}
                                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/2 h-7 bg-black rounded-b-xl z-20 flex justify-center items-center">
                                                        <div className="w-16 h-1.5 bg-gray-800 rounded-full"></div>
                                                        <div className="absolute right-6 w-2 h-2 bg-gray-800 rounded-full"></div>
                                                    </div>

                                                    {/* Side buttons */}
                                                    <div className="absolute -left-[14px] top-32 w-[6px] h-12 bg-gray-700 rounded-l-lg"></div>
                                                    <div className="absolute -left-[14px] top-48 w-[6px] h-12 bg-gray-700 rounded-l-lg"></div>
                                                    <div className="absolute -right-[14px] top-32 w-[6px] h-16 bg-gray-700 rounded-r-lg"></div>

                                                    {/* Status bar */}
                                                    <div className="absolute top-0 left-0 right-0 h-7 z-10 flex justify-between items-center px-5">
                                                        <div className="text-white text-xs">9:41</div>
                                                        <div className="flex items-center space-x-1">
                                                            <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                            <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                            <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                            <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                        </div>
                                                    </div>

                                                    {/* Content iframe */}
                                                    <iframe
                                                        ref={iframeRef}
                                                        title="Mobile Preview"
                                                        className="w-full h-full bg-white"
                                                        style={{
                                                            borderRadius: '30px',
                                                            border: 'none'
                                                        }}
                                                    ></iframe>

                                                    {/* Home indicator */}
                                                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1/3 h-1 bg-white rounded-full z-20"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    {policy?.url ? (
                                        <div className="w-full">
                                            <div className="bg-gray-50 border rounded-lg p-4 mb-4 flex flex-col sm:flex-row justify-between items-center">
                                                <div className="flex items-center mb-3 sm:mb-0">
                                                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                                                        <MdDevices className="text-blue-600 text-xl" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-bold text-gray-800">
                                                            {policy.url.split('/').pop()}
                                                        </h3>
                                                        <p className="text-sm text-gray-500">
                                                            Current terms & conditions file
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="text-xs bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full font-medium">
                                                        Mobile View
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Mobile phone display */}
                                            <div className="bg-gray-800 p-6 rounded-lg flex justify-center items-center">
                                                <div className="relative">
                                                    {/* Phone outer case */}
                                                    <div className="absolute inset-0 bg-black rounded-[40px] shadow-2xl transform scale-[1.03] border border-gray-700"></div>

                                                    {/* Phone inner frame */}
                                                    <div className="relative bg-black rounded-[36px] overflow-hidden border-[8px] border-black" style={{ width: '375px', height: '700px' }}>
                                                        {/* Notch */}
                                                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/2 h-7 bg-black rounded-b-xl z-20 flex justify-center items-center">
                                                            <div className="w-16 h-1.5 bg-gray-800 rounded-full"></div>
                                                            <div className="absolute right-6 w-2 h-2 bg-gray-800 rounded-full"></div>
                                                        </div>

                                                        {/* Side buttons */}
                                                        <div className="absolute -left-[14px] top-32 w-[6px] h-12 bg-gray-700 rounded-l-lg"></div>
                                                        <div className="absolute -left-[14px] top-48 w-[6px] h-12 bg-gray-700 rounded-l-lg"></div>
                                                        <div className="absolute -right-[14px] top-32 w-[6px] h-16 bg-gray-700 rounded-r-lg"></div>

                                                        {/* Status bar */}
                                                        <div className="absolute top-0 left-0 right-0 h-7 z-10 flex justify-between items-center px-5">
                                                            <div className="text-white text-xs">9:41</div>
                                                            <div className="flex items-center space-x-1">
                                                                <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                                <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                                <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                                <div className="h-2.5 w-2.5 bg-white rounded-full"></div>
                                                            </div>
                                                        </div>

                                                        {/* Content iframe */}
                                                        <iframe
                                                            src={policy.url}
                                                            title="Mobile Terms View"
                                                            className="w-full h-full bg-white"
                                                            style={{
                                                                borderRadius: '30px',
                                                                border: 'none'
                                                            }}
                                                        ></iframe>

                                                        {/* Home indicator */}
                                                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1/3 h-1 bg-white rounded-full z-20"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                            <MdUpload className="mx-auto text-gray-400 text-5xl mb-3" />
                                            <p className="text-gray-500 text-lg">No terms & conditions file has been uploaded yet.</p>
                                            <p className="text-gray-400 text-sm mt-1">Click "Edit" to upload your terms & conditions HTML file.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default Terms;